import { useCallback, useEffect, useId, useLayoutEffect, useMemo, useRef, useState } from 'react'
import PropTypes from 'prop-types'
import { stripTitleHTML, createMarkup } from '../../helpers/markup'
import { dataLayerPush } from '../../helpers/thirdparty'

const scrollAlignPx = 120

/** Umbraco True/False and occasional PascalCase / string payloads from embedded JSON. */
function isAutoDetectSectionsOn(value) {
  if (value === true || value === 1) {
    return true
  }
  if (typeof value === 'string') {
    return value.toLowerCase() === 'true'
  }
  return false
}

function linkLabelText(label) {
  if (!label) {
    return ''
  }
  const markup = createMarkup(label)
  if (!markup) {
    return label
  }
  return stripTitleHTML(markup)
}

function getJumpScanRoot(fromElement) {
  if (!fromElement) {
    return null
  }
  const scoped = fromElement.closest('[data-jump-scan-root]')
  if (scoped) {
    return scoped
  }
  return document.getElementById('main')
}

function followsInDocument(anchor, node) {
  return Boolean(anchor.compareDocumentPosition(node) & Node.DOCUMENT_POSITION_FOLLOWING)
}

/** Only h2s inside section title/description RTE blocks are jump targets (h3+ ignored). */
const jumpHeadingSelector = '[data-type="section-title"] h2, [data-type="section-description"] h2'

function slugify(text) {
  return text
    .toLowerCase()
    .trim()
    .replace(/[^\w\s-]/g, '')
    .replace(/\s+/g, '-')
    .replace(/-+/g, '-')
    .slice(0, 80)
}

/**
 * RTE headings have no id, so give each target a stable, unique one to anchor to. Reuse an
 * existing id when present; otherwise inject a slug derived from the text (deterministic, so it
 * stays stable across re-scans). Also set scroll-margin so the heading clears the sticky header.
 */
function ensureHeadingId(heading, seen) {
  let id = heading.id?.trim()
  if (!id) {
    const base = `jump-${slugify(heading.textContent || '')}` || 'jump-section'
    id = base
    let n = 2
    while (seen.has(id) || (document.getElementById(id) && document.getElementById(id) !== heading)) {
      id = `${base}-${n}`
      n += 1
    }
    heading.id = id
  }
  heading.style.scrollMarginTop = `${scrollAlignPx}px`
  return id
}

function collectAutoTargets(root, jumpHost) {
  if (!root || !jumpHost) {
    return []
  }

  // Reference point for "later in the document": the jump menu's own component wrapper (if any),
  // otherwise the nav host itself. Only headings that follow this become jump targets.
  const anchorWrapper = jumpHost.closest('[id^="component-"]')
  const positionRef = anchorWrapper ?? jumpHost
  const seen = new Set()
  const candidates = []

  // querySelectorAll returns matches in document order, so no extra sort is needed. Each
  // section-title / section-description block may contain multiple h2s — every one is a target.
  root.querySelectorAll(jumpHeadingSelector).forEach((heading) => {
    if (jumpHost.contains(heading)) {
      return
    }
    if (!followsInDocument(positionRef, heading)) {
      return
    }
    const label = heading.textContent?.trim()
    if (!label) {
      return
    }
    const id = ensureHeadingId(heading, seen)
    if (seen.has(id)) {
      return
    }
    seen.add(id)
    candidates.push({ id, label })
  })

  return candidates
}

function findActiveTargetId(targets) {
  if (!targets.length) {
    return ''
  }
  for (let i = targets.length - 1; i >= 0; i -= 1) {
    const el = document.getElementById(targets[i].id)
    if (!el) {
      continue
    }
    const rect = el.getBoundingClientRect()
    if (rect.top <= scrollAlignPx) {
      return targets[i].id
    }
  }
  return targets[0].id
}

function JumpToSection(props) {
  const { title, autoDetectSections, manualSections, AutoDetectSections } = props
  const useAutoDetect = isAutoDetectSectionsOn(autoDetectSections ?? AutoDetectSections)
  const rootRef = useRef(null)
  const titleId = useId()
  const [autoTargets, setAutoTargets] = useState([])
  const [activeId, setActiveId] = useState('')

  const manualTargets = useMemo(() => {
    if (!manualSections?.length) {
      return []
    }
    return manualSections
      .map((row) => ({
        id: row.anchorId?.trim(),
        label: row.label?.trim(),
      }))
      .filter((row) => row.id && row.label)
  }, [manualSections])

  const refreshAutoTargets = useCallback(() => {
    const host = rootRef.current
    if (!host || !useAutoDetect) {
      return
    }
    const scanRoot = getJumpScanRoot(host)
    if (!scanRoot) {
      setAutoTargets([])
      return
    }
    setAutoTargets(collectAutoTargets(scanRoot, host))
  }, [useAutoDetect])

  useLayoutEffect(() => {
    if (!useAutoDetect) {
      setAutoTargets([])
      return undefined
    }
    refreshAutoTargets()
    const raf1 = requestAnimationFrame(() => {
      requestAnimationFrame(refreshAutoTargets)
    })
    const onLoad = () => refreshAutoTargets()
    window.addEventListener('load', onLoad)

    let observer
    let moFrame
    const raf2 = requestAnimationFrame(() => {
      const host = rootRef.current
      if (!host) {
        return
      }
      const scanRoot = getJumpScanRoot(host)
      if (!scanRoot) {
        return
      }
      observer = new MutationObserver(() => {
        if (moFrame) {
          cancelAnimationFrame(moFrame)
        }
        moFrame = requestAnimationFrame(() => {
          moFrame = undefined
          refreshAutoTargets()
        })
      })
      observer.observe(scanRoot, { childList: true, subtree: true })
    })

    return () => {
      cancelAnimationFrame(raf1)
      cancelAnimationFrame(raf2)
      if (moFrame) {
        cancelAnimationFrame(moFrame)
      }
      window.removeEventListener('load', onLoad)
      observer?.disconnect()
    }
  }, [useAutoDetect, refreshAutoTargets])

  /** Auto-detect on: use DOM-scanned links only; manual rows are ignored (not merged). Auto off: manual only. */
  const targets = useMemo(() => {
    if (useAutoDetect) {
      return autoTargets
    }
    return manualTargets
  }, [useAutoDetect, autoTargets, manualTargets])

  useEffect(() => {
    if (!targets.length) {
      setActiveId('')
      return
    }
    setActiveId(findActiveTargetId(targets))
  }, [targets])

  useEffect(() => {
    if (!targets.length) {
      return undefined
    }
    let frame
    const onScroll = () => {
      if (frame) {
        cancelAnimationFrame(frame)
      }
      frame = requestAnimationFrame(() => {
        setActiveId(findActiveTargetId(targets))
      })
    }
    window.addEventListener('scroll', onScroll, { passive: true })
    onScroll()
    return () => {
      window.removeEventListener('scroll', onScroll)
      if (frame) {
        cancelAnimationFrame(frame)
      }
    }
  }, [targets])

  const headingText = title?.trim() || 'Jump to a section'

  const handleLinkClick = (event, targetId, label) => {
    event.preventDefault()
    dataLayerPush({
      event: 'navigation_in_page_click',
      click_text: label,
    })
    const el = document.getElementById(targetId)
    if (!el) {
      return
    }
    const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches
    el.scrollIntoView({ behavior: prefersReducedMotion ? 'auto' : 'smooth', block: 'start' })
    setActiveId(targetId)
  }

  const showNav = targets.length > 0

  const navBlock = (
    <nav
      className="rounded-xl border-2 border-sb-jump-section-border bg-sb-jump-section-bg px-6 py-5 shadow-sm"
      aria-labelledby={titleId}
    >
      <h2 id={titleId} className="text-lg font-light text-sb-jump-section-heading-text mb-4">
        {headingText}
      </h2>
      <ol className="list-decimal list-inside space-y-2 pl-1 marker:font-medium marker:text-sb-jump-section-text">
        {targets.map((item) => {
          const isActive = activeId === item.id
          return (
            <li key={item.id} className="pl-1">
              <a
                href={`#${item.id}`}
                className={`inline text-sb-jump-section-text underline decoration-sb-jump-section-border/40   rounded-sm focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-(--sb-focus-ring-color) ${
                  isActive
                    ? 'font-semibold decoration-sb-jump-section-border'
                    : 'font-light hover:decoration-sb-jump-section-border'
                }`}
                aria-current={isActive ? 'true' : undefined}
                onClick={(e) => handleLinkClick(e, item.id, linkLabelText(item.label))}
              >
                {linkLabelText(item.label)}
              </a>
            </li>
          )
        })}
      </ol>
    </nav>
  )

  /** Match the horizontal alignment every other block gets from `_Section`'s inner container, then cap the box width. */
  const alignmentClass = 'container mx-auto px-5 lg:px-8 xl:px-0 my-8'

  /** Manual mode: no DOM scan; hide when there are no rows. */
  if (!useAutoDetect) {
    if (!showNav) {
      return null
    }
    return (
      <div className={alignmentClass} data-type="jumpToSection" data-jump-to-section-nav>
        <div className="w-full sm:max-w-xl">{navBlock}</div>
      </div>
    )
  }

  /**
   * Auto-detect must mount `rootRef` before targets exist. Returning null first left ref unset, so
   * `refreshAutoTargets` never ran (deadlock). Use a visually hidden host until the scan finds sections.
   */
  return (
    <div
      ref={rootRef}
      className={showNav ? alignmentClass : 'sr-only'}
      data-type="jumpToSection"
      data-jump-to-section-nav={showNav ? true : undefined}
      aria-hidden={showNav ? undefined : 'true'}
    >
      {showNav ? <div className="w-full sm:max-w-xl">{navBlock}</div> : null}
    </div>
  )
}

JumpToSection.propTypes = {
  title: PropTypes.string,
  autoDetectSections: PropTypes.oneOfType([PropTypes.bool, PropTypes.string, PropTypes.number]),
  AutoDetectSections: PropTypes.oneOfType([PropTypes.bool, PropTypes.string, PropTypes.number]),
  manualSections: PropTypes.arrayOf(
    PropTypes.shape({
      label: PropTypes.string,
      anchorId: PropTypes.string,
    }),
  ),
}

export default JumpToSection
